import React from "react";

//Matches "[label](url)" first so a labelled link is not consumed by the bare url alternative.
const LINK_PATTERN = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+)\)|(https?:\/\/[^\s<>"')\]]+)/gi;

const isSafeUrl = (value: string): boolean => {
    try {
        const protocol = new URL(value).protocol;
        return protocol === "http:" || protocol === "https:";
    }
    catch {
        return false;
    }
}

/**
 * Renders plain text with embedded http/https addresses turned into links.
 * Supports a bare address or "[label](url)" for friendly link text.
 * Returns React nodes rather than markup so the surrounding text stays escaped.
 */
export const renderLinkedText = (text?: string): React.ReactNode => {
    if (!text) {
        return null;
    }

    //Declared per call because a shared global regex would carry lastIndex between notes.
    const pattern = new RegExp(LINK_PATTERN.source, LINK_PATTERN.flags);

    const nodes: React.ReactNode[] = [];
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = pattern.exec(text)) !== null) {
        if (match.index > lastIndex) {
            nodes.push(
                <React.Fragment key={`text-${lastIndex}`}>{text.slice(lastIndex, match.index)}</React.Fragment>
            );
        }

        const url = match[2] ?? match[3];
        const label = match[1] ?? url;

        if (isSafeUrl(url)) {
            nodes.push(
                <a key={`link-${match.index}`} href={url} target="_blank" rel="noopener noreferrer">
                    {label}
                </a>
            );
        }
        else {
            nodes.push(<React.Fragment key={`raw-${match.index}`}>{match[0]}</React.Fragment>);
        }

        lastIndex = pattern.lastIndex;
    }

    if (lastIndex < text.length) {
        nodes.push(<React.Fragment key={`text-${lastIndex}`}>{text.slice(lastIndex)}</React.Fragment>);
    }

    return nodes;
}
